import z from "zod";
import {
  CreateShelfPayloadSchema,
  CreateShelfRowPayloadSchema,
  UpdateShelfRowPayloadSchema
} from "../hooks/shelveHooks";
import { apiClient } from "../commonApis/apiClient";
import { useMutation } from "@tanstack/react-query";
import { fetch5 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;
export interface Shelf {
  id: string;
  location: { lat: number; lng: number };
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  storageType?: "B2C" | "B2B";
}

export interface ShelfRow {
  id: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CreateShelfPayload = z.infer<typeof CreateShelfPayloadSchema>;

export interface UpdateShelfPayload {
  location?: { lat: number; lng: number };
  name?: string;
  isActive?: boolean;
}

export type CreateShelfRowPayload = z.infer<typeof CreateShelfRowPayloadSchema>;

export type UpdateShelfRowPayload = z.infer<
  typeof UpdateShelfRowPayloadSchema
>;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}
interface UpdateShelveStatusPayload {
  id: string;
  status: "ACTIVE" | "INACTIVE";
  type: "rack" | "level" | "shelve" ;
}

export const useUpdateShelveStatusMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateShelveStatusPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/outlet/shelve/update/status?type=outlet`,
        "PATCH",
        {
          id: payload.id,
          type: payload.type,
          status: payload.status,
        },
      );

      if (!res.success) {
        throw new Error(res.message || "Status update failed");
      }

      return res;
    },
  });
};

export const shelfApi = {
  // Shelf CRUD
  createShelf: (
    payload: CreateShelfPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<Shelf>(
      "/api/outlet/shelve?type=outlet",
      payload,
      isFormData,
    ),
  getShelves: (page: number, searchFor: string) =>
    apiClient.get<Shelf[]>(`/api/shelve?page=${page}&searchFor=${searchFor}`),
  getAllShelves: () =>
    apiClient.get<Shelf[]>(`/api/outlet/shelve/all?type=outlet`),
  getShelfById: (id: string) => apiClient.get<Shelf>(`/api/shelve/${id}`),
  updateShelf: (id: string, payload: UpdateShelfPayload) =>
    apiClient.patch<Shelf>(`/api/outlet/shelve/${id}?type=outlet`, payload),
  updateShelfFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<Shelf>(`/api/shelve/${id}`, payload, true),
  deleteShelf: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/${id}`, payload),
  downloadShelveBarcode: (outletShelveId: string) =>
    apiClient.get<{ pdfUrl: string }>(
      `/api/outlet/shelve/${outletShelveId}/download-barcode?type=outlet`,
    ),
  // Shelf Row CRUD
  createShelfRow: (
    payload: CreateShelfRowPayload | FormData,
    isFormData: boolean = false,
  ) =>
    apiClient.post<ShelfRow>(
      "/api/outlet/shelve/rack-rows?type=outlet",
      payload,
      isFormData,
    ),
  getShelfRows: (id: string) =>
    apiClient.get<ShelfRow[]>(`/api/outlet/shelve/rack-rows/${id}/racks?type=outlet`),
  getShelfRowById: (id: string) =>
    apiClient.get<ShelfRow>(`/api/shelve/row/${id}`),
  updateShelfRow: (id: string, payload: UpdateShelfRowPayload) =>
    apiClient.patch<ShelfRow>(`/api/outlet/shelve/rack-rows/${id}?type=outlet`, payload),
  updateShelfRowFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRow>(`/api/shelve/row/${id}`, payload, true),
  deleteShelfRow: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/outlet/row/rowInstance/${id}?type=outlet`),
  printLabels: (id: string) =>
    apiClient.get<void>(`/api/shelve/outlet/${id}/download-barcode?type=outlet`),

};
